import {Editor} from '@tiptap/react'
import {
  CellSelection,
  TableMap,
  selectionCell,
  columnIsHeader,
  rowIsHeader,
} from '@tiptap/pm/tables'

type Direction = 'up' | 'down' | 'left' | 'right'

function getTableContext(editor: Editor) {
  const {state} = editor
  const $cell = selectionCell(state)
  const table = $cell.node(-1)
  const tableStart = $cell.start(-1)
  const map = TableMap.get(table)
  const cellPos = $cell.pos - tableStart
  const cellIndex = map.map.indexOf(cellPos)
  const row = Math.floor(cellIndex / map.width)
  const col = cellIndex % map.width
  return {state, $cell, table, tableStart, map, cellPos, row, col}
}

function getAdjacentCellPos(
  map: TableMap,
  row: number,
  col: number,
  direction: Direction,
): number | null {
  let targetRow = row
  let targetCol = col
  switch (direction) {
    case 'up':
      targetRow = row - 1
      break
    case 'down':
      targetRow = row + 1
      break
    case 'left':
      targetCol = col - 1
      break
    case 'right':
      targetCol = col + 1
      break
  }
  if (
    targetRow < 0 ||
    targetRow >= map.height ||
    targetCol < 0 ||
    targetCol >= map.width
  ) {
    return null
  }
  return map.map[targetRow * map.width + targetCol]
}

export function selectColumn(editor: Editor): boolean {
  try {
    const {state, tableStart, map, col} = getTableContext(editor)
    const firstCellPos = map.map[col]
    const lastCellPos = map.map[(map.height - 1) * map.width + col]
    const $first = state.doc.resolve(tableStart + firstCellPos)
    const $last = state.doc.resolve(tableStart + lastCellPos)
    const selection = CellSelection.colSelection($first, $last)
    editor.view.dispatch(state.tr.setSelection(selection))
    requestAnimationFrame(() => editor.view.focus())
    return true
  } catch {
    return false
  }
}

export function selectRow(editor: Editor): boolean {
  try {
    const {state, tableStart, map, row} = getTableContext(editor)
    const firstCellPos = map.map[row * map.width]
    const lastCellPos = map.map[row * map.width + (map.width - 1)]
    const $first = state.doc.resolve(tableStart + firstCellPos)
    const $last = state.doc.resolve(tableStart + lastCellPos)
    const selection = CellSelection.rowSelection($first, $last)
    editor.view.dispatch(state.tr.setSelection(selection))
    requestAnimationFrame(() => editor.view.focus())
    return true
  } catch {
    return false
  }
}

export function canMergeCellInDirection(
  editor: Editor,
  direction: Direction,
): boolean {
  try {
    const {map, row, col, cellPos} = getTableContext(editor)
    const adjacentPos = getAdjacentCellPos(map, row, col, direction)
    if (adjacentPos === null) return false
    // Can't merge if adjacent cell is same cell (already spanning)
    return adjacentPos !== cellPos
  } catch {
    return false
  }
}

export function mergeCellInDirection(
  editor: Editor,
  direction: Direction,
): boolean {
  try {
    const {state, tableStart, map, row, col, cellPos} = getTableContext(editor)
    const adjacentPos = getAdjacentCellPos(map, row, col, direction)
    if (adjacentPos === null || adjacentPos === cellPos) return false

    const $anchor = state.doc.resolve(tableStart + cellPos)
    const $head = state.doc.resolve(tableStart + adjacentPos)
    const selection = CellSelection.create(state.doc, $anchor.pos, $head.pos)
    editor.view.dispatch(state.tr.setSelection(selection))
    return editor.commands.mergeCells()
  } catch {
    return false
  }
}

export function isHeaderRowActive(editor: Editor): boolean {
  try {
    const {map, table} = getTableContext(editor)
    return rowIsHeader(map, table, 0)
  } catch {
    return false
  }
}

export function isHeaderColumnActive(editor: Editor): boolean {
  try {
    const {map, table} = getTableContext(editor)
    return columnIsHeader(map, table, 0)
  } catch {
    return false
  }
}
